import { defineStore } from 'pinia';
import mitt, { type Emitter } from 'mitt';
import yaml from 'yaml';

import {
  type ComputeProviderConfig,
  type ComputeProviderInfo,
  ComputeProviderName,
  type Deployment,
  DeploymentState,
  type Instance,
  UserSubscriptionItem,
} from '@clovyr/pollen';
import { getComputeProvider } from '@clovyr/pollen/compute/util';
import type { CreateDeploymentResponse } from '@clovyr/pollen/deployment';
import { WNextAccessAPI } from '@clovyr/pollen/deployment/access';
import { WNextLiteLLMAPI } from '@clovyr/pollen/deployment/litellm';
import { WNextDeployment } from '@clovyr/pollen/deployment/wnext';
import { isFullyPropagated } from '@clovyr/pollen/dns/query';
import { WNextDNS } from '@clovyr/pollen/dns/wnext';
import { getHostByID } from '@clovyr/pollen/fixtures/hosts';
import {
  addBackupValues,
  CLOVYR_CONFIGURE_KEY,
  CLOVYR_CUSTOM_DOMAIN,
  CLOVYR_DOCKER_PROXY,
  CLOVYR_PRIMARY_FQDN,
  type Manifest,
  NewValues,
  type Values,
} from '@clovyr/pollen/manifest';
import { isDNSProxied } from '@clovyr/pollen/manifest/configure/util';
import {
  addScheme,
  exec,
  httpClient,
  type LaunchEvents,
  waitForInstanceReady,
} from '@clovyr/pollen/remote_exec';
import { type SalesPricingContext, SubscriptionAPI } from '@clovyr/pollen/subscription';
import { sleep } from '@clovyr/pollen/util/sleep';

import { useAppVersion } from '@/composables/useAppVersion';
import { useBeforeUnload } from '@/composables/useBeforeUnload';
import { config, useInstanceLauncher } from '@/composables/useInstanceLauncher';
import { PaymentCallbackSubItem, usePayments } from '@/composables/usePayments';
import { useRemoteExec } from '@/composables/useRemoteExec';
import { config as appConfig } from '@/init/config';

import { addAnswers, type AnswerMap } from './launch_settings';
import { usePollenStore } from './pollen_store';
import { addLocalSsoValues } from './local_sso';
import { rehydrate, removeStorage } from './util';

export type LaunchStatus = 'todo' | 'working' | 'success';
export interface Task {
  description: string;
  status: LaunchStatus;
}

export function initTasks(computeProviderName?: string): Task[] {
  return [
    {
      description: computeProviderName
        ? `Requesting private server on ${computeProviderName}`
        : 'Requesting private server',
      status: 'todo',
    },
    {
      description: 'Waiting for server to become available',
      status: 'todo',
    },
    {
      description: 'Deploying',
      status: 'todo',
    },
    {
      description: 'Finishing up',
      status: 'todo',
    },
  ];
}

export interface Launch {
  // state
  currentTaskIndex: number;
  tasks: Task[];
  instance: Instance | undefined;
  emitter: Emitter<LaunchEvents>;
  isResume?: boolean;

  // inputs
  manifest: Manifest;
  deployment: Deployment;
  selectedHost: ComputeProviderInfo;
  selectedComputeProviderConfig: ComputeProviderConfig;
  answers: AnswerMap;
  salesContext?: SalesPricingContext;
}

const LITELLM_USAGE_SUBSCRIPTION_SLUG = 'clovyr_ai_llm_usage';

type LeafLaunchStatus = 'unknown' | 'not-ready' | 'ready' | 'bootstrap' | 'api';

interface LeafStatusResponse {
  mode?: string;
  status?: string;
}

/**
 * Manages active launches (deployments)
 */
export const useLaunchStore = defineStore('launch', () => {
  const pollenStore = usePollenStore();

  type Launches = Record<string, Launch>;
  const launches = ref<Launches>({});

  /* -------------------------------------------------------------------------- */
  /*                              INTERNAL methods                              */
  /* -------------------------------------------------------------------------- */

  // These methods should be safe to accept the launch object directly

  /**
   * launch instance in compute provider, do not deploy
   */
  async function launchInstance(launch: Launch): Promise<Instance> {
    if (!launch.selectedHost) {
      return Promise.reject(new Error('No host selected'));
    }
    if (!launch.selectedComputeProviderConfig) {
      return Promise.reject(new Error('No compute provider configuration present'));
    }
    if (!launch.deployment.region) {
      return Promise.reject(new Error('No region configured'));
    }

    // providers
    const computeProvider = getComputeProvider(launch.deployment.hostingProvider);
    computeProvider.setConfig(launch.selectedComputeProviderConfig);
    const dnsProvider = new WNextDNS();

    const proxyDNS = isDNSProxied(launch.manifest, launch.deployment.fqdn);

    const instance = await useInstanceLauncher().launchInstance(
      computeProvider,
      dnsProvider,
      proxyDNS,
      launch.deployment.region,
      launch.manifest.deployment.compute?.size,
      launch.deployment.fqdn,
    );

    return instance;
  }

  /**
   * deploy to an existing instance
   */
  async function deployInstance(launch: Launch, values: Values): Promise<Instance> {
    if (!launch.instance) {
      return Promise.reject(new Error('No instance'));
    }

    return useInstanceLauncher().deployInstance(
      launch.manifest,
      launch.instance,
      values,
      launch.emitter,
      new WNextDNS(),
    );
  }

  /**
   * Try to claim an instant launch instance. Returns true on success.
   */
  async function claimInstance(launch: Launch): Promise<Instance> {
    let res: CreateDeploymentResponse;
    try {
      res = await new WNextDeployment().createDeployment({
        appName: launch.manifest.metadata.id,
      });
    } catch {
      // console.warn('failed to claim instance:', error);
      return Promise.reject(new Error('failed to claim instance'));
    }

    // use it
    launch.instance = res.instance;
    if (launch.instance.configurekey) {
      launch.deployment.accessList.push(launch.instance.configurekey);
    }

    return launch.instance;
  }

  /**
   * Check if user has entered/touched any answers. Ignores defaults that may be present in the
   * answer map.
   *
   * @param launch
   * @returns
   */
  function hasAnswers(launch: Launch): boolean {
    return (
      !!launch.answers &&
      Object.keys(launch.answers).length > 0 &&
      Object.values(launch.answers).some((v) => v.isTouched)
    );
  }

  /**
   * Returns true if the launch is eligible to claim an instant launch instance.
   *
   * True if:
   * - No instance is already assigned to the launch
   * - Either the launch is not a resume, or the deployment has no FQDN (meaning it was never launched)
   * - The deployment's hosting provider is WNext
   * - The launch has no answers, or the manifest is for Clovyr AI Cloud (which can have answers)
   * - The deployment has no custom domain
   * - The deployment has no region, or the region matches the default region for the selected host
   *
   * @param launch
   * @returns
   */
  function shouldClaimInstantLaunch(launch: Launch): boolean {
    return (
      !launch.instance &&
      (!launch.isResume || !launch.deployment.fqdn) &&
      launch.deployment.hostingProvider === ComputeProviderName.WNext &&
      (!hasAnswers(launch) || launch.manifest.metadata.id === 'clovyr-ai-cloud') &&
      !launch.deployment.customDomain &&
      (!launch.deployment.region || launch.deployment.region === launch.selectedHost.defaultRegion)
    );
  }

  /**
   * Check for settings other than a configure key or custom domain (stored early on)
   *
   * @param launch
   * @returns
   */
  function hasAppSettings(launch: Launch): boolean {
    const keys = Object.keys(launch.deployment.appSettings || {}).filter(
      (k) => k !== CLOVYR_CONFIGURE_KEY && k !== CLOVYR_CUSTOM_DOMAIN,
    );
    return keys.length > 0;
  }

  function hasDeploymentAccessKey(launch: Launch): boolean {
    return (
      !!launch.deployment.accessList?.[0] ||
      typeof launch.deployment.appSettings?.[CLOVYR_CONFIGURE_KEY] === 'string'
    );
  }

  function getLaunchRemoteExec(launch: Launch) {
    if (launch.deployment.fqdn && hasDeploymentAccessKey(launch)) {
      return useRemoteExec(launch.deployment, launch.manifest, undefined, {
        allowNonRunning: true,
      });
    }
    if (launch.instance?.fqdn && launch.instance.configurekey) {
      return useRemoteExec(launch.instance, launch.manifest, launch.deployment.appSettings, {
        allowNonRunning: true,
      });
    }
    return undefined;
  }

  /**
   * Get the status of a launch's leaf server.
   *
   * Possible return values:
   * - 'unknown': no leaf server found or could not be reached
   * - 'not-ready': leaf server found but not ready to accept requests
   * - 'ready': leaf server is ready to accept requests
   * - 'bootstrap': leaf server is in bootstrap mode (not yet configured)
   * - 'api': leaf server is in API mode (configured and ready)
   *
   * Should not raise an exception, even if server is down.
   *
   * @param launch
   * @returns
   */
  async function getLeafLaunchStatus(launch: Launch): Promise<LeafLaunchStatus> {
    const remoteExec = getLaunchRemoteExec(launch);
    const host = remoteExec?.creds.value.host;
    const accessKey = remoteExec?.creds.value.accessKey;
    if (!host) {
      return 'unknown';
    }

    // ensure hostname resolves DNS first
    if (!(await isFullyPropagated(host))) {
      return 'unknown';
    }

    const headers: Record<string, string> = { accept: 'application/json' };
    if (accessKey) {
      headers.Authorization = `Bearer ${accessKey}`;
    }

    let readyRes: Awaited<ReturnType<typeof httpClient.get>>;
    try {
      readyRes = await httpClient.get(`${addScheme(host)}/_leaf/ready`, { headers });
    } catch {
      return 'unknown';
    }
    if (!(readyRes instanceof Response) || !readyRes.ok) {
      return 'not-ready';
    }

    try {
      const statusRes = await httpClient.get(`${addScheme(host)}/_leaf/status`, { headers });
      if (statusRes instanceof Response && statusRes.ok) {
        const status = (await statusRes.json()) as LeafStatusResponse;
        if (status.mode === 'api') {
          return 'api';
        }
        if (status.mode === 'bootstrap' && status.status === 'ok') {
          return 'bootstrap';
        }
      }
    } catch {
      // The ready endpoint is enough to continue with a configure retry.
    }

    return 'ready';
  }

  async function restoreLaunchInstance(launch: Launch): Promise<void> {
    if (launch.instance || !launch.deployment.fqdn) {
      return;
    }

    const existingInstance = pollenStore.getInstanceByFQDN(launch.deployment.fqdn);
    if (launch.deployment.hostingProvider !== ComputeProviderName.WNext) {
      if (existingInstance) {
        launch.instance = existingInstance;
      }
      return;
    }

    const instance = await getComputeProvider(ComputeProviderName.WNext).getInstance({
      id: launch.deployment.fqdn,
    });
    if (!instance) {
      launch.instance = undefined;
      return;
    }

    instance.configurekey =
      existingInstance?.configurekey ||
      instance.configurekey ||
      launch.deployment.accessList?.[0] ||
      (launch.deployment.appSettings?.[CLOVYR_CONFIGURE_KEY] as string | undefined);

    if (existingInstance) {
      Object.assign(existingInstance, instance);
      launch.instance = existingInstance;
    } else {
      launch.instance = instance;
    }

    await pollenStore.pollen.putInstance(launch.instance);
  }

  /* -------------------------------------------------------------------------- */
  /*                            End INTERNAL methods                            */
  /* -------------------------------------------------------------------------- */

  function setTaskProgress(launch: Launch, currentTaskIndex: number) {
    launch.currentTaskIndex = Math.min(Math.max(currentTaskIndex, 0), launch.tasks.length);
    launch.tasks.forEach((task, index) => {
      if (index < launch.currentTaskIndex) {
        task.status = 'success';
      } else if (
        index === launch.currentTaskIndex &&
        launch.currentTaskIndex < launch.tasks.length
      ) {
        task.status = 'working';
      } else {
        task.status = 'todo';
      }
    });
  }

  function getTaskIndexForDeploymentState(state: DeploymentState): number {
    switch (state) {
      case DeploymentState.WaitingInstance:
        return 1;
      case DeploymentState.LaunchedInstance:
      case DeploymentState.UpdatingDNS:
        return 2;
      case DeploymentState.Deploying:
        return 3;
      case DeploymentState.Running:
        return 4;
      default:
        return 0;
    }
  }

  function isResumableLaunchState(state: DeploymentState): boolean {
    switch (state) {
      case DeploymentState.CreatingDNS:
      case DeploymentState.LaunchingInstance:
      case DeploymentState.WaitingInstance:
      case DeploymentState.LaunchedInstance:
      case DeploymentState.UpdatingDNS:
      case DeploymentState.Deploying:
        return true;
      default:
        return false;
    }
  }

  function completeCurrentStep(deploymentID: string) {
    const launch = launches.value[deploymentID];
    if (!launch) {
      return;
    }
    setTaskProgress(launch, launch.currentTaskIndex + 1);
  }

  /**
   * Set user's email in deployment configs
   */
  function addUserEmail(manifest: Manifest, values: Values, email: string | undefined) {
    if (!email || !manifest?.config?.deployment) {
      return;
    }
    Object.entries(manifest.config.deployment).forEach(([configName, conf]) => {
      if (
        conf.value?.type === 'string' &&
        conf.value.format === 'email' &&
        conf.value.default &&
        conf.value.default === values[configName]
      ) {
        values[configName] = email;
      }
    });
  }

  /**
   * Request and add a LiteLLM key to the deployment values if the manifest indicates to use it.
   *
   * Currently only used by Clovyr AI Cloud.
   *
   * @param manifest
   * @param values
   * @returns
   */
  async function ensureLiteLLMSubscriptionItem(
    deployment: Deployment,
    payments: ReturnType<typeof usePayments>,
  ): Promise<void> {
    const sub = await payments.getSubscription(true);
    if (!sub) {
      throw new Error('failed to get subscription for LiteLLM usage tracking');
    }

    const resourceID = `litellm_${deployment.id}`;
    const subItems = await payments.listSubscriptionItems();
    const existingSubItem = subItems.find((item) => item.resource_id === resourceID);
    if (existingSubItem) {
      payments.subscriptionItems.value[existingSubItem.id] = existingSubItem;
      return;
    }

    const subItem = await new SubscriptionAPI().addItem({
      subscriptionID: sub.id,
      resourceID,
      appSlug: LITELLM_USAGE_SUBSCRIPTION_SLUG,
      isTrial: false,
    });
    payments.subscriptionItems.value[subItem.id] = subItem;
  }

  /**
   * Request a LiteLLM key and add to deployment values if manifest indicates to use LiteLLM. Also
   * ensures the subscription item for tracking usage is created.
   *
   * @param manifest
   * @param deployment
   * @param values
   * @param payments
   * @returns
   */
  async function addLiteLLMKey(
    manifest: Manifest,
    deployment: Deployment,
    values: Values,
    payments: ReturnType<typeof usePayments>,
  ): Promise<void> {
    const use = !!manifest?.config?.advanced?.USE_CLOVYR_LLM && values.USE_CLOVYR_LLM === true;
    if (!use) {
      return;
    }

    try {
      await ensureLiteLLMSubscriptionItem(deployment, payments);
    } catch (err) {
      // FIXME: remove after stripe fix
      console.warn(err);
    }

    // request a key and add to values
    const ltk = await new WNextLiteLLMAPI().createLiteLLMKey();
    values.CLOVYR_LITELLM_URL = ltk.url;
    values.CLOVYR_LITELLM_KEY = ltk.key;

    // hack: also set LLM_IMAGE for clovyr ai in staging
    if (
      manifest.metadata.id === 'clovyr-ai-cloud' &&
      values.LLM_IMAGE &&
      appConfig.VITE_APP_ENV !== 'production'
    ) {
      values.LLM_IMAGE = 'clovyr/ai-cloud:dev-esgz';
    }
  }

  async function addAICloudSsoValues(launch: Launch, values: Values): Promise<void> {
    if (launch.manifest.metadata.id !== 'clovyr-ai-cloud') {
      return;
    }
    const primaryFQDN = values[CLOVYR_PRIMARY_FQDN];
    if (typeof primaryFQDN !== 'string') {
      throw new Error('missing AI Cloud primary domain');
    }
    const audience = `https://${primaryFQDN}`;
    values.CLOVYR_PUBLIC_URL = audience;
    if (values.CLOVYR_SSO_ENABLED !== true) {
      return;
    }
    addLocalSsoValues(
      values,
      launch.deployment.id,
      audience,
      appConfig.VITE_PUBLIC_URL,
      await pollenStore.pollen.listPasskeys(),
    );
  }

  /**
   * Generate app config values based on manifest, user answers, and other factors like backup and
   * LiteLLM keys.
   *
   * Optionally accepts an existing values object to mutate, otherwise creates a new one. This
   * allows it to be used both for initial config generation before deployment, and for generating
   * updated config (e.g. with LiteLLM key) after deployment.
   *
   * @param launch
   * @param payments
   * @param values
   * @returns
   */
  async function generateAppConfig(
    launch: Launch,
    payments: ReturnType<typeof usePayments>,
    values?: Values,
  ): Promise<Values> {
    const fqdn = launch.instance!.fqdn!;
    const accessKey = await new WNextAccessAPI().createAccessKey({ fqdn });

    if (!values) {
      values = {};
    }

    addAnswers(launch.manifest, launch.answers, values);
    NewValues(values, launch.manifest, fqdn, launch.deployment.customDomain);
    await addAICloudSsoValues(launch, values);
    addBackupValues(
      values,
      `${config.s3URL}/${launch.deployment.id}`,
      accessKey,
      appConfig.VITE_PUBLIC_URL,
      launch.instance!.configurekey!,
    );
    addUserEmail(launch.manifest, values, pollenStore.email);
    await addLiteLLMKey(launch.manifest, launch.deployment, values, payments);
    // add docker registry proxy only for wnext
    if (
      launch.deployment.hostingProvider === ComputeProviderName.WNext &&
      appConfig.VITE_DOCKER_PROXY
    ) {
      values[CLOVYR_DOCKER_PROXY] = appConfig.VITE_DOCKER_PROXY;
    }
    return values;
  }

  async function ensureAppConfig(
    launch: Launch,
    payments: ReturnType<typeof usePayments>,
  ): Promise<Values> {
    if (hasAppSettings(launch)) {
      return launch.deployment.appSettings;
    }

    const values = await generateAppConfig(launch, payments);
    launch.deployment.appSettings = values;
    await pollenStore.pollen.putDeployment(launch.deployment);
    return values;
  }

  /**
   * Sync configured app settings from the leaf server to the deployment. If the
   * deployment already has app settings, this is a no-op.
   *
   * @param launch
   * @param payments
   */
  async function syncConfiguredAppSettings(
    launch: Launch,
    payments: ReturnType<typeof usePayments>,
  ): Promise<void> {
    let values = launch.deployment.appSettings;
    if (!hasAppSettings(launch)) {
      const remoteExec = getLaunchRemoteExec(launch);
      if (!remoteExec || !(await remoteExec.ensureHostLogin())) {
        throw new Error('No remote access available for configured app');
      }

      const { host, accessKey } = remoteExec.creds.value;
      const res = await exec(
        host,
        accessKey,
        `cat /opt/clovyr/apps/${launch.deployment.appID}/clovyr-values.yml`,
        true,
        true,
      );
      values = yaml.parse(await res.text());
      launch.deployment.appSettings = values;
    }

    if (launch.manifest.metadata.id === 'clovyr-ai-cloud') {
      await generateAppConfig(launch, payments, values);
      await useAppVersion(launch.deployment.libraryItemID ?? launch.deployment.id).runAppVersion(
        launch.manifest,
      );
    }

    await pollenStore.pollen.putDeployment(launch.deployment);
  }

  async function ensureSubscriptionItem(
    launch: Launch,
    payments: ReturnType<typeof usePayments>,
  ): Promise<void> {
    const { appID } = launch.deployment;
    const existingSubItem: UserSubscriptionItem | null = rehydrate(PaymentCallbackSubItem);
    let subItem: UserSubscriptionItem | undefined;
    if (
      payments.subscription.value &&
      existingSubItem &&
      existingSubItem.resource_id === `launching_${appID}`
    ) {
      const subItems = await new SubscriptionAPI().listSubscriptionItems(
        payments.subscription.value.id,
      );
      subItem = subItems.find((item) => item.resource_id === `launching_${appID}`);
      if (subItem) {
        await payments.updateSubscriptionItem(subItem, launch.deployment, launch.salesContext);
        removeStorage(PaymentCallbackSubItem);
        launch.deployment.subscriptionItemID = subItem.id;
        await pollenStore.pollen.putDeployment(launch.deployment);
      }
    }
    if (!subItem) {
      void payments.addSubscriptionItem(launch.deployment, true, launch.salesContext);
    }
  }

  async function rotateKeys(launch: Launch): Promise<void> {
    const oldKey = launch.instance!.configurekey;
    if (oldKey && !launch.deployment.accessList.includes(oldKey)) {
      launch.deployment.accessList.push(oldKey);
    }

    const { ensureHostLogin } = getLaunchRemoteExec(launch)!;
    if (!(await ensureHostLogin())) {
      console.warn('Host login failed, remote exec may not work');
    }

    if (oldKey) {
      const newKey = await useInstanceLauncher().rotateKeys(
        launch.instance!,
        launch.manifest,
        launch.deployment.appSettings,
      );
      if (newKey) {
        launch.deployment.accessList = launch.deployment.accessList.filter((k) => k !== oldKey);
        launch.deployment.accessList.push(newKey);
        launch.deployment.appSettings[CLOVYR_CONFIGURE_KEY] = newKey;
        launch.deployment.keys.configureKey = newKey;
      }
    }

    await pollenStore.pollen.putDeployment(launch.deployment);
  }

  /**
   * Finish the launch by marking it complete and updating deployment state to
   * running. Runs post-launch tasks like upgrading clovyr services if applicable.
   *
   * @param launch
   */
  async function finishLaunch(launch: Launch): Promise<void> {
    if (!launch.instance) {
      throw new Error('No instance');
    }

    if (launch.currentTaskIndex < 3) {
      setTaskProgress(launch, 3);
    }
    launch.deployment.state = DeploymentState.Running;

    await pollenStore.pollen.putDeployment(launch.deployment);
    await pollenStore.pollen.putInstance(launch.instance);

    setTaskProgress(launch, launch.tasks.length);
    useBeforeUnload().removeBlocker(`deployment/${launch.deployment.id}`);
    console.timeEnd(`launch ${launch.deployment.id}`);

    // after instance is up, try to upgrade clovyr services immediately
    void useAppVersion(
      launch.deployment.libraryItemID ?? launch.deployment.id,
    ).runPostLaunchTasks();
  }

  /**
   * Start the launch, either instant or on-demand.
   *
   * @param launch
   */
  async function startLaunch(deploymentID: string): Promise<void> {
    const launch = launches.value[deploymentID];
    if (!launch) {
      throw new Error(`No launch found for deployment ${deploymentID}`);
    }
    const payments = usePayments();

    // analytics - not tied to user, currently
    if (!launch.isResume) {
      void new SubscriptionAPI().recordAppLaunch(
        launch.manifest.metadata.id,
        launch.deployment.hostingProvider,
        launch.deployment.dnsProvider,
        launch.salesContext,
      );
    }

    // always load deployment details
    // (previously we only did this for on-demand launches, but instant launches may need to be redeployed)
    launch.manifest = await pollenStore.garden.loadDeploymentDetails(launch.manifest);
    await restoreLaunchInstance(launch);

    if (launch.isResume && launch.instance) {
      // check if leaf server is already up and running, in which case we can skip to syncing app settings
      // (this may happen when resuming a launch and our local deployment state is out of sync with the server)
      const serverStatus = await getLeafLaunchStatus(launch);
      if (serverStatus === 'api') {
        await ensureSubscriptionItem(launch, payments);
        await syncConfiguredAppSettings(launch, payments);
        await finishLaunch(launch);
        launch.emitter.emit('launch:app_deployed');
        return;
      }
    }

    // see if we can claim an instant-launch instance
    // must have chosen wnext & no answers (unless it's clovyr ai cloud, which can have answers)
    let claimed = false;
    if (shouldClaimInstantLaunch(launch)) {
      try {
        await claimInstance(launch);
        claimed = true;
      } catch {
        // no-op, instance unavailable or other reason, continue with on-demand launch
      }
    }

    if (!launch.instance) {
      try {
        // launch new instance (either skipped claim or failed (none available))
        launch.instance = await launchInstance(launch);
        launch.deployment.state = DeploymentState.WaitingInstance;
        if (launch.deployment.hostingProvider !== ComputeProviderName.WNext) {
          // save config that was used to launch
          await pollenStore.pollen.saveComputeProviderConfig(launch.selectedComputeProviderConfig);
        }
      } catch (e) {
        console.error('error launching instance:', e);
        launch.deployment.state = DeploymentState.LaunchFailed;
        await pollenStore.pollen.putDeployment(launch.deployment);
        return;
      }
    }

    // update with new details
    launch.deployment.fqdn = launch.instance.fqdn;
    launch.deployment.appSettings ||= {};
    if (launch.deployment.customDomain) {
      launch.deployment.appSettings[CLOVYR_CUSTOM_DOMAIN] = launch.deployment.customDomain;
    }
    await pollenStore.pollen.putDeployment(launch.deployment);
    await pollenStore.pollen.putInstance(launch.instance);

    // wait for instance to be ready (regardless of instant or on-demand launch)
    const { host } = getLaunchRemoteExec(launch)!;
    await waitForInstanceReady({
      fqdn: host,
      configureKey: launch.instance.configurekey!,
      reconfigure: claimed, // quicker dns check if we claimed an instant-launch instance, since it should already be configured
    });
    launch.emitter.emit('launch:instance_up');

    // rotate immediately
    await rotateKeys(launch);

    setTaskProgress(launch, Math.max(1, getTaskIndexForDeploymentState(launch.deployment.state)));

    await ensureSubscriptionItem(launch, payments);

    const serverStatus = await getLeafLaunchStatus(launch);
    if (serverStatus === 'api' || claimed) {
      // if we claimed an instant-launch instance, we may have skipped the waiting step and the server is already up
      // in that case, we can skip to syncing app settings and finishing the launch
      // (if clovyr-ai-cloud, we will also redeploy with custom settings)
      await syncConfiguredAppSettings(launch, payments);

      // quickly mark all steps complete for instant claims or already-configured resumes
      for (let index = launch.currentTaskIndex; index < launch.tasks.length; index++) {
        // eslint-disable-next-line no-await-in-loop
        await sleep(50);
        setTaskProgress(launch, index + 1);
      }

      await finishLaunch(launch);
      launch.emitter.emit('launch:app_deployed');
      return;
    }

    const values = await ensureAppConfig(launch, payments);
    await deployInstance(launch, values);
    await finishLaunch(launch);
  }

  function bindLaunchEventHandlers(launch: Launch) {
    const { id } = launch.deployment;

    launch.emitter.on('launch:instance_up', async () => {
      launch.deployment.state = DeploymentState.LaunchedInstance;
      await pollenStore.pollen.putDeployment(launch.deployment);
      setTaskProgress(launch, Math.max(launch.currentTaskIndex, 2));
    });

    launch.emitter.on('launch:app_deploying', async () => {
      launch.deployment.state = DeploymentState.Deploying;
      await pollenStore.pollen.putDeployment(launch.deployment);
      setTaskProgress(launch, Math.max(launch.currentTaskIndex, 3));
    });

    launch.emitter.on('launch:app_deployed', async () => {
      launch.deployment.state = DeploymentState.Running;
      await pollenStore.pollen.putDeployment(launch.deployment);
      setTaskProgress(launch, launch.tasks.length);
      useBeforeUnload().removeBlocker(`deployment/${id}`);
    });

    launch.emitter.on('launch:err', (err) => {
      useBeforeUnload().removeBlocker(`deployment/${id}`);
      console.error('launch error', err);
    });
  }

  /**
   * Create launch and add deployment to pollen but do not start
   */
  async function createLaunch(
    manifest: Manifest,
    deployment: Deployment,
    selectedHost: ComputeProviderInfo,
    selectedComputeProviderConfig: ComputeProviderConfig,
    answers: AnswerMap,
    salesContext?: SalesPricingContext,
  ): Promise<Launch> {
    const launch = {
      // state
      currentTaskIndex: 0,
      tasks: initTasks(selectedHost.title),
      instance: undefined,
      emitter: mitt<LaunchEvents>(),
      isResume: false,

      // inputs
      manifest,
      deployment,
      selectedHost,
      selectedComputeProviderConfig,
      answers,
      salesContext,
    };

    useBeforeUnload().addBlocker(`deployment/${deployment.id}`);
    console.time(`launch ${deployment.id}`);
    bindLaunchEventHandlers(launch);

    launch.deployment.state = DeploymentState.LaunchingInstance;
    setTaskProgress(launch, 0);

    await pollenStore.pollen.putDeployment(launch.deployment);
    launches.value[deployment.id] = launch;

    // must return the ref, not the plain object
    return launches.value[deployment.id];
  }

  // Try to resume launching the given deployment.
  // If the deployment is not in a resumable state, throws an error.
  async function createResumeLaunch(deploymentID: string): Promise<Launch> {
    const deployment = pollenStore.getDeploymentByLibraryRoute(deploymentID);
    if (!deployment) {
      throw new Error(`No deployment found for ${deploymentID}`);
    }
    const existingLaunch = launches.value[deployment.id];
    if (existingLaunch && existingLaunch.deployment.libraryItemID === deployment.libraryItemID) {
      return existingLaunch;
    }
    if (!isResumableLaunchState(deployment.state)) {
      throw new Error(`Deployment ${deploymentID} is not in a resumable launch state`);
    }

    const manifest = pollenStore.getDeploymentApp(deployment);
    if (!manifest) {
      throw new Error(`No app manifest found for deployment ${deploymentID}`);
    }

    const selectedHost = getHostByID(deployment.hostingProvider);
    if (!selectedHost) {
      throw new Error(`No host found for ${deployment.hostingProvider}`);
    }

    const computeProvider = getComputeProvider(deployment.hostingProvider);
    const selectedComputeProviderConfig =
      pollenStore.getComputeProviderConfig(deployment) || computeProvider.getConfig();
    if (!selectedComputeProviderConfig) {
      throw new Error(`No compute provider configuration found for ${deployment.hostingProvider}`);
    }

    const launch: Launch = {
      currentTaskIndex: 0,
      tasks: initTasks(selectedHost.title),
      instance: undefined,
      emitter: mitt<LaunchEvents>(),
      isResume: true,

      manifest,
      deployment,
      selectedHost,
      selectedComputeProviderConfig,
      answers: {},
    };

    await restoreLaunchInstance(launch);
    setTaskProgress(launch, getTaskIndexForDeploymentState(deployment.state));
    useBeforeUnload().addBlocker(`deployment/${deployment.id}`);
    console.time(`launch ${deployment.id}`);
    bindLaunchEventHandlers(launch);

    launches.value[deployment.id] = launch;
    return launches.value[deployment.id];
  }

  async function resumeLaunch(deploymentID: string): Promise<void> {
    const launch = await createResumeLaunch(deploymentID);
    await startLaunch(launch.deployment.id);
  }

  function getLaunchForDeployment(deploymentID: string): Launch | undefined {
    return launches.value[deploymentID];
  }

  function deleteLaunch(deploymentID: string) {
    delete launches.value[deploymentID];
  }

  return {
    launches,

    completeCurrentStep,
    getLaunchForDeployment,
    deleteLaunch,
    createLaunch,
    resumeLaunch,
    startLaunch,
  };
});
